import { Stack, Typography as JoyTypography } from '@mui/joy';
import * as yup from 'yup';
import EthereumLogo from 'cryptocurrency-icons/svg/color/eth.svg?react';
import mapValues from 'lodash/fp/mapValues';
import type { ReactElement } from 'react';
import type { SvgIcon } from '@mui/material';

import BitcoinLogo from './bitcoin.svg?react';
import AploLogo from './aplo.svg?react';
import BinanceLogo from './binance.svg?react';
import BitgetLogo from './bitget.svg?react';
import BitgoLogo from './bitgo.svg?react';
import BullishLogo from './bullish.svg?react';
import BybiLogo from './bybit.svg?react';
import CoinBaseLogo from './coinbase.svg?react';
import CoinBaseBlueLetterCLogo from './coinBaseBlueLetterC.svg?react';
import CopperLogo from './copper.svg?react';
import CryptoComLogo from './cryptoCom.svg?react';
import { DefaultIPWhitelistingSection } from './DefaultIPWhitelistingSection.tsx';
import DeribitLogo from './deribit.svg?react';
import FalconXLogo from './falconx.svg?react';
import FireblocksLogo from './fireblocks.svg?react';
import FlowdeskLogo from './flowdesk.svg?react';
import FtxLogo from './ftx.svg?react';
import GateLogo from './gate.svg?react';
import HuobiLogo from './huobi.svg?react';
import HyperliquidLogo from './hyperliquid.svg?react';
import IBKRLogo from './ibkr.svg?react';
import KrakenLogo from './kraken.svg?react';
import KucoinLogo from './kucoin.svg?react';
import MexcLogo from './mexc.svg?react';
import OKXLogo from './okx.svg?react';
import OrbitLogo from './orbit.svg?react';
import PhemexLogo from './phemex.svg?react';
import DriftLogo from './drift.svg?react';
import { SelectableIPAddresses } from './SelectableIPAddresses.tsx';
import SolanaLogo from './solana.svg?react';
import DyDxLogo from './dydx.svg?react';
import sFoxLogo from './sFox.svg?react';
import UnknownVenueLogo from './unknownExchange.svg?react';
import { commonBlack, commonWhite, neutralPlainDisabledColor } from '../../theme/colors';
import { formatLabelToName, normalizeParts } from '../formatter.utils';
import { yupWhen } from '../../technical/validation.ts';
import BinanceV2MasterCredentials from '../portfolio/account/createAccount/BinanceV2MasterCredentials.tsx';

export interface VenueField {
  label: string;
  name: string;
  type?: string;
  optional?: boolean;
}

export interface VenueEntry {
  name: string;
  type: 'blockchain' | 'cex' | 'otc' | 'non-crypto' | 'unknown';
  tooltip?: string;
  symbol: string;
  cannotConnect?: boolean;
  icon: (() => ReactElement) | typeof SvgIcon;
  iconBackground: string;
  baseFields?: VenueField[];
  getFieldsSchema: () => yup.ObjectSchema<object>;
  disclaimer: () => ReactElement;
  adminOnly?: boolean;
  extraFields?: () => ReactElement;
  uiOnlyFields: string[];
  onboarding?: {
    assets?: boolean;
    recalculateHistoricalBalance?: boolean;
  };
}

export const unknownVenueLabel = 'ex:unknown';
export const GENIE_VENUE = 'dpr:genie';
export const IBKR_VENUE = 'cex:ibkr';

const unknownVenue: VenueEntry = {
  name: 'Unknown',
  symbol: 'N/A',
  cannotConnect: true,
  type: 'unknown',
  icon: UnknownVenueLogo,
  iconBackground: neutralPlainDisabledColor,
  disclaimer: DefaultIPWhitelistingSection,
  getFieldsSchema: () => yup.object(),
  uiOnlyFields: [],
};

const formatLabelToSymbol = (name: Label): string => {
  if (!name) {
    return '';
  }

  const parts = normalizeParts(name);
  const lastPart = parts.at(-1);
  if (!lastPart) {
    throw new Error('Empty path');
  }

  return lastPart.toUpperCase();
};

export const createUnsupportedVenueEntry = (label: Label): VenueEntry => {
  return {
    ...unknownVenue,
    name: formatLabelToName(label),
    symbol: formatLabelToSymbol(label),
  };
};

const emptyDisclaimer = (): ReactElement => <></>;
export const rawVenues: Record<
  string,
  Omit<VenueEntry, 'disclaimer' | 'getFieldsSchema' | 'uiOnlyFields'> & {
    disclaimer?: VenueEntry['disclaimer'];
    getFieldsSchema?: () => yup.ObjectSchema<object>;
    uiOnlyFields?: string[];
  }
> = {
  'cex:aplo': {
    name: 'APLO',
    symbol: 'APLO',
    type: 'cex',
    icon: AploLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Account id', name: 'account_id' },
    ],
  },
  'cex:aplo-v2': {
    name: 'APLO',
    symbol: 'APLO',
    type: 'cex',
    icon: AploLogo,
    iconBackground: commonBlack,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Account id', name: 'account_id' },
    ],
  },
  'cex:binance': {
    name: 'Binance',
    symbol: 'BIN',
    type: 'cex',
    icon: BinanceLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
    // onboarding: {
    //   assets: true,
    //   recalculateHistoricalBalance: true,
    // },
    disclaimer: (): ReactElement => {
      return (
        <Stack spacing={1.5}>
          <JoyTypography color="neutral" level="body-md" maxWidth={500}>
            IP whitelisting is required to receive derivative information from the exchange. Without it we will be only
            able to track spots. Please whitelist the following IP addresses:
          </JoyTypography>
          <SelectableIPAddresses />
        </Stack>
      );
    },
  },
  'cex:binance-v2': {
    name: 'Binance',
    symbol: 'BIN',
    type: 'cex',
    icon: BinanceLogo,
    iconBackground: commonBlack,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
      { label: 'Email', name: 'email', optional: true },
    ],
    extraFields: BinanceV2MasterCredentials,
    getFieldsSchema: () => {
      const trimmedString = yup.string().trim().required();
      return yup.object({
        api_key: trimmedString,
        secret: trimmedString,
        use_master_credentials: yup.boolean().default(false), // snake case for consistency, not send to the backend
        master_key: yupWhen(['use_master_credentials'], ([useMasterCredentials]): yup.Schema => {
          if (useMasterCredentials) {
            return yup.object({
              api_key: trimmedString,
              secret: trimmedString,
            });
          }

          return yup.mixed().notRequired();
        }),
      });
    },
    disclaimer: (): ReactElement => {
      return (
        <Stack spacing={1.5}>
          <JoyTypography color="neutral" level="body-md" maxWidth={500}>
            IP whitelisting is required to receive derivative information from the exchange. Without it we will be only
            able to track spots. Please whitelist the following IP addresses:
          </JoyTypography>
          <SelectableIPAddresses />
        </Stack>
      );
    },
  },
  'cex:binanceus': {
    name: 'Binance US',
    symbol: 'BIN US',
    type: 'cex',
    icon: BinanceLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:bitget': {
    name: 'Bitget',
    symbol: 'Bitget',
    type: 'cex',
    icon: BitgetLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
      { label: 'Passphrase', name: 'password', type: 'password' },
    ],
  },
  'cex:bitget-v2': {
    name: 'Bitget',
    symbol: 'Bitget',
    type: 'cex',
    icon: BitgetLogo,
    iconBackground: commonBlack,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
      { label: 'Passphrase', name: 'password', type: 'password' },
    ],
  },
  'cex:bullish': {
    name: 'Bullish',
    symbol: 'Bullish',
    type: 'cex',
    icon: BullishLogo,
    iconBackground: commonBlack,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
      { label: 'ECDSA Metadata', name: 'metadata', optional: true },
      { label: 'Server (prod/sandbox)', name: 'server' },
    ],
  },
  'cex:bybit': {
    name: 'Bybit',
    symbol: 'Bybit',
    type: 'cex',
    icon: BybiLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:bybit-v2': {
    name: 'Bybit',
    symbol: 'Bybit',
    type: 'cex',
    icon: BybiLogo,
    iconBackground: commonBlack,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:coinbaseprime': {
    name: 'Coinbase Prime',
    symbol: 'Coinbase Prime',
    type: 'cex',
    icon: CoinBaseLogo,
    iconBackground: '#0052FF',
    cannotConnect: true,
    baseFields: [
      { label: 'Access key', name: 'api_key' },
      { label: 'Signing key', name: 'secret', type: 'password' },
      { label: 'Passphrase', name: 'password', type: 'password' },
      { label: 'Service account id', name: 'account_id' },
    ],
  },
  'cex:coinbaseprime-v2': {
    name: 'Coinbase Prime',
    symbol: 'Coinbase Prime',
    type: 'cex',
    icon: CoinBaseLogo,
    iconBackground: '#0052FF',
    baseFields: [
      { label: 'Access key', name: 'api_key' },
      { label: 'Signing key', name: 'secret', type: 'password' },
      { label: 'Passphrase', name: 'password', type: 'password' },
      { label: 'Service account id', name: 'account_id' },
    ],
  },
  'cex:coinbaseexchange': {
    name: 'Coinbase Exchange',
    symbol: 'Coinbase Exchange',
    type: 'cex',
    icon: CoinBaseLogo,
    iconBackground: '#0052FF',
    baseFields: [
      { label: 'Access key', name: 'api_key' },
      { label: 'Signing key', name: 'secret', type: 'password' },
      { label: 'Passphrase', name: 'password', type: 'password' },
    ],
  },
  'cex:copper': {
    name: 'Copper',
    symbol: 'Copper',
    type: 'cex',
    icon: CopperLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:coinbasepro': {
    name: 'Coinbase Pro',
    symbol: 'Coinbase Pro',
    type: 'cex',
    icon: CoinBaseLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
      { label: 'Password', name: 'password', type: 'password' },
    ],
  },
  'cex:coinbase': {
    name: 'Coinbase',
    symbol: 'Coinbase',
    type: 'cex',
    icon: CoinBaseBlueLetterCLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:coinbaseadvanced': {
    name: 'Coinbase Advanced',
    symbol: 'Coinbase Advanced',
    type: 'cex',
    icon: CoinBaseLogo,
    iconBackground: commonWhite,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:cryptocom': {
    name: 'Crypto.com',
    symbol: 'Crypto.com',
    type: 'cex',
    icon: CryptoComLogo,
    iconBackground: commonWhite,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:debank': {
    name: 'EVM',
    type: 'blockchain',
    symbol: 'ETH',
    icon: EthereumLogo,
    iconBackground: '#687fed',
    cannotConnect: true,
    baseFields: [{ label: 'Public address', name: 'public_id' }],
    // empty disclaimer. By default, if the property isn't defined, we show
    // information about ip whitelisting, but it's not needed here
    disclaimer: (): ReactElement => <></>,
  },
  'cex:debank-v2': {
    name: 'EVM',
    type: 'blockchain',
    symbol: 'EVM',
    icon: EthereumLogo,
    iconBackground: '#687fed',
    baseFields: [{ label: 'Public address', name: 'public_id' }],
    // empty disclaimer. By default, if the property isn't defined, we show
    // information about ip whitelisting, but it's not needed here
    disclaimer: (): ReactElement => <></>,
  },
  'cex:deribit': {
    name: 'Deribit',
    symbol: 'Deribit',
    type: 'cex',
    icon: DeribitLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:deribit-v2': {
    name: 'Deribit',
    symbol: 'Deribit',
    type: 'cex',
    icon: DeribitLogo,
    iconBackground: commonBlack,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:dydx': {
    name: 'DyDx',
    type: 'blockchain',
    symbol: 'DyDx',
    icon: DyDxLogo,
    iconBackground: commonBlack,
    baseFields: [
      { label: 'Passphrase', name: 'password', type: 'password' },
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
    // empty disclaimer. By default, if the property isn't defined, we show
    // information about ip whitelisting, but it's not needed here
    disclaimer: (): ReactElement => <></>,
  },
  'cex:falconx': {
    name: 'Falcon X',
    symbol: 'FalconX',
    type: 'cex',
    icon: FalconXLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
      { label: 'Password', name: 'password', type: 'password' },
    ],
  },
  'cex:falconx-v2': {
    name: 'Falcon X',
    symbol: 'FalconX',
    type: 'cex',
    icon: FalconXLogo,
    iconBackground: commonBlack,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
      { label: 'Password', name: 'password', type: 'password' },
    ],
  },
  'cex:fireblocks': {
    name: 'Fireblocks',
    symbol: 'Fireblocks',
    type: 'cex',
    icon: FireblocksLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Private key', name: 'secret', type: 'password' },
    ],
    disclaimer: () => (
      <>
        <DefaultIPWhitelistingSection />
        Please be aware that transaction information provided by Fireblocks might be incomplete.
      </>
    ),
  },
  'cex:flowdesk': {
    name: 'Flowdesk',
    symbol: 'Flowdesk',
    type: 'cex',
    icon: FlowdeskLogo,
    iconBackground: commonWhite,
    baseFields: [
      { label: 'Client id', name: 'client_id' },
      { label: 'Client name', name: 'client_name' },
      { label: 'Client secret', name: 'client_secret', type: 'password' },
    ],
  },
  'cex:ftx': {
    name: 'FTX',
    symbol: 'FTX',
    type: 'cex',
    icon: FtxLogo,
    iconBackground: commonWhite,
    cannotConnect: true,
  },
  'cex:ftxus': {
    name: 'FTX US',
    symbol: 'FTX US',
    type: 'cex',
    icon: FtxLogo,
    iconBackground: commonWhite,
    cannotConnect: true,
  },
  'cex:huobi': {
    name: 'Huobi',
    symbol: 'Huobi',
    type: 'cex',
    icon: HuobiLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:hyperliquid': {
    name: 'Hyperliquid',
    type: 'blockchain',
    symbol: 'Hyperliquid',
    icon: HyperliquidLogo,
    iconBackground: '#072722',
    baseFields: [{ label: 'Public address', name: 'public_id' }],
    // empty disclaimer. By default, if the property isn't defined, we show
    // information about ip whitelisting, but it's not needed here
    disclaimer: emptyDisclaimer,
  },
  'cex:gate': {
    name: 'Gate',
    symbol: 'Gate',
    type: 'cex',
    icon: GateLogo,
    iconBackground: commonWhite,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:kraken': {
    name: 'Kraken',
    symbol: 'Kraken',
    type: 'cex',
    icon: KrakenLogo,
    iconBackground: '#5741D9',
    cannotConnect: true,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:kraken-v2': {
    name: 'Kraken',
    symbol: 'Kraken',
    type: 'cex',
    icon: KrakenLogo,
    iconBackground: '#5741D9',
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:kucoin': {
    name: 'Kucoin',
    symbol: 'Kucoin',
    type: 'cex',
    icon: KucoinLogo,
    iconBackground: commonBlack,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
      { label: 'Password', name: 'password', type: 'password' },
      { label: 'API key version', name: 'version' },
    ],
  },
  'cex:mexc': {
    name: 'MEXC',
    symbol: 'MEXC',
    type: 'cex',
    icon: MexcLogo,
    iconBackground: commonWhite,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:bitquery_btc': {
    name: 'Bitcoin',
    type: 'blockchain',
    symbol: 'Bitcoin',
    icon: BitcoinLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [{ label: 'Public address', name: 'publicId' }],
    // empty disclaimer. By default, if the property isn't defined, we show
    // information about ip whitelisting, but it's not needed here
    disclaimer: (): ReactElement => <></>,
  },
  'cex:kucoinfutures': {
    name: 'Kucoin Futures',
    symbol: 'Kucoin',
    type: 'cex',
    icon: KucoinLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
      { label: 'Password', name: 'password', type: 'password' },
    ],
  },
  'cex:okx': {
    name: 'OKX',
    symbol: 'OKX',
    type: 'cex',
    icon: OKXLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'Password', name: 'password', type: 'password' },
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
    ],
  },
  'cex:okx-v2': {
    name: 'OKX',
    symbol: 'OKX',
    type: 'cex',
    icon: OKXLogo,
    iconBackground: commonBlack,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
      { label: 'Password', name: 'password', type: 'password' },
      { label: 'Server (us/eea/global)', name: 'server' },
    ],
  },
  'cex:orbit': {
    name: 'Orbit',
    symbol: 'Orbit',
    type: 'cex',
    icon: OrbitLogo,
    iconBackground: commonWhite,
    baseFields: [
      { label: 'API key', name: 'api_key' },
      { label: 'Secret key', name: 'secret', type: 'password' },
      { label: 'Server (prod/test)', name: 'server' },
    ],
  },
  'cex:phemex': {
    name: 'Phemex',
    symbol: 'Phemex',
    type: 'cex',
    icon: PhemexLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [
      { label: 'ID', name: 'api_key' },
      { label: 'API Secret', name: 'secret', type: 'password' },
    ],
  },
  'cex:bitgo': {
    name: 'BitGo',
    symbol: 'BitGo',
    type: 'cex',
    icon: BitgoLogo,
    iconBackground: commonWhite,
    baseFields: [{ label: 'Access Token', name: 'access_token' }],
    // empty disclaimer. By default, if the property isn't defined, we show
    // information about ip whitelisting, but it's not needed here
    disclaimer: emptyDisclaimer,
  },
  'cex:sfox': {
    name: 'sFox',
    symbol: 'sFox',
    type: 'cex',
    icon: sFoxLogo,
    iconBackground: commonBlack,
    baseFields: [{ label: 'ID', name: 'api_key' }],
    // empty disclaimer. By default, if the property isn't defined, we show
    // information about ip whitelisting, but it's not needed here
    disclaimer: emptyDisclaimer,
  },
  'cex:solana': {
    name: 'Solana',
    type: 'blockchain',
    symbol: 'Solana',
    icon: SolanaLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [{ label: 'Public address', name: 'public_id' }],
    // empty disclaimer. By default, if the property isn't defined, we show
    // information about ip whitelisting, but it's not needed here
    disclaimer: emptyDisclaimer,
  },
  'cex:solana-v2': {
    name: 'Solana',
    type: 'blockchain',
    symbol: 'Solana',
    icon: SolanaLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    baseFields: [{ label: 'Public address', name: 'public_id' }],
    // empty disclaimer. By default, if the property isn't defined, we show
    // information about ip whitelisting, but it's not needed here
    disclaimer: emptyDisclaimer,
  },
  'cex:solscan': {
    name: 'SolScan',
    type: 'blockchain',
    symbol: 'Solana',
    icon: SolanaLogo,
    iconBackground: commonBlack,
    baseFields: [{ label: 'Public address', name: 'public_id' }],
    // empty disclaimer. By default, if the property isn't defined, we show
    // information about ip whitelisting, but it's not needed here
    disclaimer: emptyDisclaimer,
  },
  'dex:drift': {
    name: 'Drift',
    type: 'blockchain',
    symbol: 'Drift',
    icon: DriftLogo,
    iconBackground: commonBlack,
    cannotConnect: true,
    // empty disclaimer. By default, if the property isn't defined, we show
    // information about ip whitelisting, but it's not needed here
    disclaimer: emptyDisclaimer,
  },
  [IBKR_VENUE]: {
    name: 'Interactive Brokers',
    symbol: 'IBKR',
    type: 'non-crypto',
    icon: IBKRLogo,
    iconBackground: commonWhite,
  },
  [GENIE_VENUE]: {
    name: 'Virtual',
    symbol: 'Virtual',
    type: 'otc',
    cannotConnect: true,
    icon: unknownVenue.icon,
    iconBackground: unknownVenue.iconBackground,
  },
  [unknownVenueLabel]: unknownVenue,
};

export const venues: Record<string, VenueEntry> = mapValues(
  (obj): VenueEntry => ({
    disclaimer: DefaultIPWhitelistingSection,
    getFieldsSchema: () => {
      const venueExtraFields = obj.baseFields ?? [];

      const fieldsSchema = Object.fromEntries(
        venueExtraFields.filter((field) => !field.optional).map((field) => [field.name, yup.string().trim().required()])
      );

      return yup.object(fieldsSchema);
    },
    uiOnlyFields: [],
    ...obj,
  }),
  rawVenues
);
